
import React, { useEffect, useState } from "react";

const GooglePosts = ({ onClose, onSuccess, onError }) => {

    const [showDeletePrompt, setShowDeletePrompt] = useState(false);
    const [topicToDelete, setTopicToDelete] = useState(null);

    const [googlePostData, setGooglePostData] = useState({
        id: null,
        category: "",
        topic: "",
        mediaUrl: ""
    });

    const [mediaFile, setMediaFile] = useState(null);

    const [googlePostErrors, setGooglePostErrors] = useState({});
    const [googlePostSubmitting, setGooglePostSubmitting] = useState(false);

    const [selectedCategory, setSelectedCategory] = useState("");

    // Review topics
    const [reviewTopics, setReviewTopics] = useState([]);
    const [reviewTopicsLoading, setReviewTopicsLoading] = useState(false);
    const [deletingTopicId, setDeletingTopicId] = useState(null);

    // Edit mode
    const [editingTopic, setEditingTopic] = useState(false);

    const googlePostCategories = [
        "dog",
        "cat",
        "birds",
        "sugar glider",
        "rabbit",
        "hamster",
        "turtle",
        "guinea pig"
    ];

    const availableCategories = [
        ...new Set(
            reviewTopics
                .map((topic) => topic.category)
                .filter(Boolean)
        )
    ].sort();

    const filteredReviewTopics = selectedCategory
        ? reviewTopics.filter(
            (topic) => topic.category === selectedCategory
        )
        : reviewTopics;

    /*
     * Fetch review topics
     */
    const fetchReviewTopics = async () => {

        setReviewTopicsLoading(true);

        try {

            const token = localStorage.getItem("token");

            if (!token) {
                throw new Error("Authentication token not found");
            }

            const res = await fetch("/v1/review-topics", {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!res.ok) {
                throw new Error(
                    `Failed to fetch review topics (${res.status})`
                );
            }

            const data = await res.json();

            setReviewTopics(data);

        } catch (err) {

            console.error(err);

            if (onError) {
                onError("Could not load review topics right now.");
            }

        } finally {
            setReviewTopicsLoading(false);
        }
    };

    /*
     * Load topics when component opens
     */
    useEffect(() => {
        fetchReviewTopics();
    }, []);

    /*
     * Handle form changes
     */
    const handleGooglePostChange = (e) => {

        const { name, value } = e.target;

        setGooglePostData((prev) => ({
            ...prev,
            [name]: value
        }));

        setGooglePostErrors((prev) => ({
            ...prev,
            [name]: ""
        }));
    };

    /*
     * Edit topic
     */
    const handleEditTopic = (topic) => {

        setEditingTopic(true);

        setGooglePostData({
            id: topic.id,
            category: topic.category || "",
            topic: topic.topic || "",
            mediaUrl: topic.mediaUrl || ""
        });

        setGooglePostErrors({});

        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    };

    /*
     * Cancel edit
     */
    const handleCancelEdit = () => {

        setEditingTopic(false);

        setGooglePostData({
            id: null,
            category: "",
            topic: "",
            mediaUrl: ""
        });

        setMediaFile(null);

        setGooglePostErrors({});

        const fileInput = document.querySelector(
            'input[name="mediaFile"]'
        );

        if (fileInput) {
            fileInput.value = "";
        }
    };

    /*
     * Submit new topic OR update existing topic
     */
    const handleGooglePostSubmit = async (e) => {

        e.preventDefault();

        const newErrors = {};

        const payload = {
            id: googlePostData.id,
            category: googlePostData.category,
            topic: googlePostData.topic.trim(),
            mediaUrl: googlePostData.mediaUrl.trim()
        };

        if (!payload.category) {
            newErrors.category = "Category is required";
        }

        if (!payload.topic) {
            newErrors.topic = "Topic is required";
        }

        // Require either URL or uploaded file
        if (!payload.mediaUrl && !mediaFile) {
            newErrors.mediaFile = "Please provide a media URL or upload a file";
        }

        setGooglePostErrors(newErrors);

        if (Object.keys(newErrors).length > 0) {
            return;
        }

        setGooglePostSubmitting(true);

        try {

            const token = localStorage.getItem("token");

            if (!token) {
                throw new Error("Authentication token not found");
            }

            const formData = new FormData();

            if (payload.id !== null) {
                formData.append("id", payload.id);
            }

            formData.append("category", payload.category);
            formData.append("topic", payload.topic);

            if (payload.mediaUrl) {
                formData.append("mediaUrl", payload.mediaUrl);
            }

            if (mediaFile) {
                formData.append("file", mediaFile);
            }

            const method = editingTopic ? "PUT" : "POST";

            const res = await fetch("/v1/review-topics", {
                method: method,
                headers: {
                    "Authorization": `Bearer ${token}`
                },
                body: formData
            });

            if (!res.ok) {
                throw new Error(
                    `Failed to ${editingTopic ? "update" : "submit"
                    } review topic (${res.status})`
                );
            }

            // Reset form
            setGooglePostData({
                id: null,
                category: "",
                topic: "",
                mediaUrl: ""
            });

            setMediaFile(null);

            setGooglePostErrors({});
            setEditingTopic(false);

            // Clear file input
            const fileInput = document.querySelector(
                'input[name="mediaFile"]'
            );

            if (fileInput) {
                fileInput.value = "";
            }

            // Refresh list
            await fetchReviewTopics();

            if (onSuccess) {
                onSuccess(
                    editingTopic
                        ? "Review topic updated successfully"
                        : "Google post topic submitted successfully"
                );
            }

        } catch (err) {

            console.error(err);

            if (onError) {
                onError(
                    editingTopic
                        ? "Could not update the review topic right now."
                        : "Could not submit Google post topic right now."
                );
            }

        } finally {
            setGooglePostSubmitting(false);
        }
    };

    /*
     * Open delete confirmation
     */
    const handleDeleteTopic = (topic) => {

        setTopicToDelete(topic);
        setShowDeletePrompt(true);
    };

    /*
     * Cancel delete
     */
    const cancelDeleteTopic = () => {

        setShowDeletePrompt(false);
        setTopicToDelete(null);
    };

    /*
     * Confirm delete
     */
    const confirmDeleteTopic = async () => {

        if (!topicToDelete) {
            return;
        }

        const topicId = topicToDelete.id;

        setShowDeletePrompt(false);
        setDeletingTopicId(topicId);

        try {

            const token = localStorage.getItem("token");

            if (!token) {
                throw new Error("Authentication token not found");
            }

            const res = await fetch(
                `/v1/review-topics/${topicId}`,
                {
                    method: "DELETE",
                    headers: {
                        "Authorization": `Bearer ${token}`
                    }
                }
            );

            if (!res.ok) {
                throw new Error(
                    `Failed to delete review topic (${res.status})`
                );
            }

            // Remove from UI
            setReviewTopics((prev) =>
                prev.filter((topic) => topic.id !== topicId)
            );

            // If deleting currently edited topic
            if (googlePostData.id === topicId) {
                handleCancelEdit();
            }

            setTopicToDelete(null);

            if (onSuccess) {
                onSuccess("Review topic deleted successfully");
            }

        } catch (err) {

            console.error(err);

            if (onError) {
                onError(
                    "Could not delete the review topic right now."
                );
            }

        } finally {
            setDeletingTopicId(null);
        }
    };

    return (
        <>
            <form
                className="google-posts-panel mb-4"
                onSubmit={handleGooglePostSubmit}
            >


                {/* ============================= */}
                {/* FORM */}
                {/* ============================= */}

                <div className="row g-3">

                    {/* Category */}
                    <div className="col-md-4">

                        <label className="form-label">
                            Category
                        </label>

                        <select
                            name="category"
                            value={googlePostData.category}
                            onChange={handleGooglePostChange}
                            className={`form-select ${googlePostErrors.category
                                ? "is-invalid"
                                : ""
                                }`}
                        >

                            <option value="">
                                Select category
                            </option>

                            {googlePostCategories.map((category) => (
                                <option
                                    key={category}
                                    value={category}
                                >
                                    {category}
                                </option>
                            ))}

                        </select>

                        {googlePostErrors.category && (
                            <div className="invalid-feedback">
                                {googlePostErrors.category}
                            </div>
                        )}

                    </div>


                    {/* Topic */}
                    <div className="col-md-8">

                        <label className="form-label">
                            Topic
                        </label>

                        <input
                            type="text"
                            name="topic"
                            value={googlePostData.topic}
                            onChange={handleGooglePostChange}
                            className={`form-control ${googlePostErrors.topic
                                ? "is-invalid"
                                : ""
                                }`}
                            placeholder="Enter Google post topic"
                        />

                        {googlePostErrors.topic && (
                            <div className="invalid-feedback">
                                {googlePostErrors.topic}
                            </div>
                        )}

                    </div>


                    {/* Media File */}
                    <div className="col-md-8">

                        <label className="form-label">
                            Upload Media
                        </label>

                        <input
                            type="file"
                            name="mediaFile"
                            accept="image/*"
                            className={`form-control ${googlePostErrors.mediaFile
                                ? "is-invalid"
                                : ""
                                }`}
                            onChange={(e) => {
                                const file = e.target.files?.[0] || null;

                                setMediaFile(file);

                                setGooglePostErrors((prev) => ({
                                    ...prev,
                                    mediaFile: ""
                                }));
                            }}
                        />

                        {googlePostErrors.mediaFile && (
                            <div className="invalid-feedback">
                                {googlePostErrors.mediaFile}
                            </div>
                        )}

                        {mediaFile && (
                            <small className="text-muted">
                                Selected: {mediaFile.name}
                            </small>
                        )}

                    </div>
                </div>


                {/* ============================= */}
                {/* FORM BUTTONS */}
                {/* ============================= */}

                <div className="mt-3 text-end">

                    {editingTopic && (
                        <button
                            type="button"
                            className="btn btn-outline-secondary me-2"
                            onClick={handleCancelEdit}
                            disabled={googlePostSubmitting}
                        >
                            Cancel
                        </button>
                    )}

                    <button
                        type="submit"
                        className={`btn ${editingTopic
                            ? "btn-primary"
                            : "btn-success"
                            }`}
                        disabled={googlePostSubmitting}
                    >

                        {googlePostSubmitting ? (
                            <>
                                <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                />

                                {editingTopic
                                    ? "Updating..."
                                    : "Submitting..."
                                }
                            </>
                        ) : (
                            editingTopic
                                ? "Update Topic"
                                : "Submit"
                        )}

                    </button>

                </div>


                {/* ============================= */}
                {/* REVIEW TOPICS */}
                {/* ============================= */}

                <div className="mt-4">

                    <div className="d-flex justify-content-between align-items-center mb-3">

                        <div>
                            <h5 className="mb-1">
                                Review Topics
                            </h5>

                            <small className="text-muted">
                                {filteredReviewTopics.length} topic
                                {filteredReviewTopics.length !== 1 ? "s" : ""}
                            </small>
                        </div>

                        <div className="d-flex align-items-center gap-2">

                            {/* Category Filter */}
                            <select
                                className="form-select form-select-sm"
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                style={{ minWidth: "160px" }}
                            >
                                <option value="">
                                    All Categories
                                </option>

                                {availableCategories.map((category) => (
                                    <option
                                        key={category}
                                        value={category}
                                    >
                                        {category
                                            .replace(/_/g, " ")
                                            .replace(/\b\w/g, (char) => char.toUpperCase())}
                                    </option>
                                ))}
                            </select>

                            {/* Refresh */}
                            <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                onClick={fetchReviewTopics}
                                disabled={reviewTopicsLoading}
                            >
                                {reviewTopicsLoading ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-1"
                                            role="status"
                                        />

                                        Loading...
                                    </>
                                ) : (
                                    "Refresh"
                                )}
                            </button>

                        </div>

                    </div>


                    {/* ============================= */}
                    {/* LOADING */}
                    {/* ============================= */}

                    {reviewTopicsLoading &&
                        reviewTopics.length === 0 && (

                            <div className="text-center py-4">

                                <span
                                    className="spinner-border"
                                    role="status"
                                />

                                <div className="mt-2 text-muted">
                                    Loading topics...
                                </div>

                            </div>

                        )}


                    {/* ============================= */}
                    {/* EMPTY */}
                    {/* ============================= */}

                    {!reviewTopicsLoading &&
                        reviewTopics.length === 0 && (

                            <div className="alert alert-light border text-center">
                                No review topics found.
                            </div>

                        )}


                    {/* ============================= */}
                    {/* SCROLLABLE TOPIC LIST */}
                    {/* ============================= */}

                    {reviewTopics.length > 0 && (

                        <div
                            className="review-topics-scroll"
                            style={{
                                maxHeight: "400px",
                                overflowY: "auto",
                                border: "1px solid #dee2e6",
                                borderRadius: "0.375rem"
                            }}
                        >

                            <div className="list-group list-group-flush">

                                {filteredReviewTopics.map((topic) => (

                                    <div
                                        key={topic.id}
                                        className={`list-group-item ${googlePostData.id === topic.id
                                            ? "bg-light"
                                            : ""
                                            }`}
                                    >

                                        <div className="d-flex justify-content-between align-items-start gap-3">

                                            {/* Topic details */}
                                            <div className="flex-grow-1">

                                                <div className="mb-1">

                                                    <span className="badge bg-primary me-2">
                                                        {topic.category}
                                                    </span>

                                                    {googlePostData.id === topic.id && (
                                                        <span className="badge bg-warning text-dark">
                                                            Editing
                                                        </span>
                                                    )}

                                                </div>

                                                <div className="fw-semibold">
                                                    {topic.topic}
                                                </div>

                                                {topic.mediaUrl && (

                                                    <div className="mt-1">

                                                        <a
                                                            href={topic.mediaUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="small text-muted"
                                                        >
                                                            View media
                                                        </a>

                                                    </div>

                                                )}

                                            </div>


                                            {/* ============================= */}
                                            {/* ACTION BUTTONS */}
                                            {/* ============================= */}

                                            <div className="d-flex gap-2 flex-shrink-0">

                                                <button
                                                    type="button"
                                                    className="btn btn-outline-primary btn-sm"
                                                    onClick={() =>
                                                        handleEditTopic(topic)
                                                    }
                                                    disabled={
                                                        deletingTopicId === topic.id ||
                                                        googlePostSubmitting
                                                    }
                                                >
                                                    Edit
                                                </button>

                                                <button
                                                    type="button"
                                                    className="btn btn-outline-danger btn-sm"
                                                    onClick={() =>
                                                        handleDeleteTopic(topic)
                                                    }
                                                    disabled={
                                                        deletingTopicId === topic.id
                                                    }
                                                >

                                                    {deletingTopicId === topic.id ? (
                                                        <>
                                                            <span
                                                                className="spinner-border spinner-border-sm me-1"
                                                                role="status"
                                                            />

                                                            Deleting...
                                                        </>
                                                    ) : (
                                                        "Delete"
                                                    )}

                                                </button>

                                            </div>

                                        </div>

                                    </div>

                                ))}

                            </div>

                        </div>

                    )}

                </div>

            </form>


            {/* ===================================== */}
            {/* DELETE CONFIRMATION MODAL */}
            {/* ===================================== */}

            {showDeletePrompt && topicToDelete && (

                <div
                    className="modal fade show"
                    style={{
                        display: "block",
                        backgroundColor: "rgba(0, 0, 0, 0.5)"
                    }}
                    role="dialog"
                    aria-modal="true"
                >

                    <div className="modal-dialog modal-dialog-centered">

                        <div className="modal-content">

                            {/* Modal header */}
                            <div className="modal-header">

                                <h5 className="modal-title">
                                    Delete Review Topic
                                </h5>

                                <button
                                    type="button"
                                    className="btn-close"
                                    aria-label="Close"
                                    onClick={cancelDeleteTopic}
                                />

                            </div>


                            {/* Modal body */}
                            <div className="modal-body">

                                <p className="mb-2">
                                    Are you sure you want to delete this topic?
                                </p>

                                <div className="alert alert-light border mb-0">

                                    <strong>
                                        {topicToDelete.topic}
                                    </strong>

                                    <div className="mt-2">

                                        <span className="badge bg-primary">
                                            {topicToDelete.category}
                                        </span>

                                    </div>

                                </div>

                            </div>


                            {/* Modal footer */}
                            <div className="modal-footer">

                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={cancelDeleteTopic}
                                >
                                    Cancel
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={confirmDeleteTopic}
                                    disabled={deletingTopicId !== null}
                                >

                                    {deletingTopicId === topicToDelete.id ? (
                                        <>
                                            <span
                                                className="spinner-border spinner-border-sm me-2"
                                                role="status"
                                            />

                                            Deleting...
                                        </>
                                    ) : (
                                        "Delete"
                                    )}

                                </button>

                            </div>

                        </div>

                    </div>

                </div>

            )}

        </>
    );
};

export default GooglePosts;

